import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { Plus, Trash, Folder, FolderOpen, CaretRight, CaretDown, PencilSimple, FolderPlus, PaperPlaneTilt, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Modal, Field, ModalFooter } from "./Contacts";

const sumStats = (list) => {
  const t = { recipients: 0, sent: 0, bounced: 0, unsubscribed: 0, unique_opens: 0, unique_clicks: 0 };
  list.forEach(c => {
    const s = c.stats || {};
    t.recipients += s.recipients || 0;
    t.sent += s.sent || 0;
    t.bounced += s.bounced || 0;
    t.unsubscribed += s.unsubscribed || 0;
    t.unique_opens += s.unique_opens || 0;
    t.unique_clicks += s.unique_clicks || 0;
  });
  return t;
};

const pct = (n, d) => (d ? ((n / d) * 100).toFixed(1) : "0.0");

export default function CampaignsPage() {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [newFolder, setNewFolder] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("cmp-folders-collapsed") || "[]")); } catch { return new Set(); }
  });
  const nav = useNavigate();
  const load = () => Promise.all([api.listCampaigns().then(setItems), api.listCampaignGroups().then(setGroups)]);
  useEffect(() => { load(); }, []);

  const toggle = (gid) => setCollapsed(prev => {
    const next = new Set(prev);
    next.has(gid) ? next.delete(gid) : next.add(gid);
    localStorage.setItem("cmp-folders-collapsed", JSON.stringify([...next]));
    return next;
  });

  const byGroup = useMemo(() => {
    const m = {};
    items.forEach(c => { if (c.group_id) (m[c.group_id] = m[c.group_id] || []).push(c); });
    return m;
  }, [items]);
  const ungrouped = useMemo(() => items.filter(c => !c.group_id), [items]);

  const moveCampaign = async (cid, gid) => {
    await api.updateCampaign(cid, { group_id: gid || null });
    toast.success(gid ? "Moved into folder" : "Removed from folder");
    load();
  };
  const renameFolder = async (g) => {
    const name = window.prompt("Folder name", g.name);
    if (!name || !name.trim() || name.trim() === g.name) return;
    await api.renameCampaignGroup(g.id, name.trim());
    load();
  };
  const deleteFolder = async (g) => {
    if (!window.confirm(`Delete folder "${g.name}"? The campaigns inside are kept and just ungrouped.`)) return;
    await api.deleteCampaignGroup(g.id);
    toast.success("Folder deleted — campaigns kept");
    load();
  };
  const nextWave = async (g) => {
    try {
      const r = await api.nextWave(g.id);
      toast.success(`Draft created for ${r.list_name} (${r.list_members} contacts) — review and send`);
      nav(`/campaigns/${r.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the next wave");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-medium tracking-tight">Campaigns</h2>
          <p className="text-sm text-zinc-500 mt-0.5">Send broadcasts, track engagement. Folders roll up stats across sends.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="btn-new-folder" onClick={() => setNewFolder(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 text-zinc-700 hover:border-zinc-400"><FolderPlus size={14}/> New folder</button>
          <button data-testid="btn-new-campaign" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800"><Plus size={14}/> New campaign</button>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs text-zinc-500 border-b border-zinc-200">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Name</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-right px-3 py-2 font-medium">Recipients</th>
              <th className="text-right px-3 py-2 font-medium">Sent</th>
              <th className="text-right px-3 py-2 font-medium">Bounced</th>
              <th className="text-right px-3 py-2 font-medium">Unsubs</th>
              <th className="text-right px-3 py-2 font-medium">Open rate</th>
              <th className="text-right px-3 py-2 font-medium">Click rate</th>
              <th className="text-right px-3 py-2"/>
            </tr>
          </thead>
          <tbody>
            {groups.map(g => {
              const members = byGroup[g.id] || [];
              const t = sumStats(members);
              const isCollapsed = collapsed.has(g.id);
              return [
                <tr key={g.id} data-testid={`folder-row-${g.name}`} onClick={() => toggle(g.id)}
                  className="border-b border-zinc-100 bg-zinc-50/80 hover:bg-zinc-100/80 cursor-pointer">
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2 font-medium text-zinc-900">
                      {isCollapsed ? <CaretRight size={12} className="text-zinc-400"/> : <CaretDown size={12} className="text-zinc-400"/>}
                      {isCollapsed ? <Folder size={15} className="text-sky-600" weight="fill"/> : <FolderOpen size={15} className="text-sky-600" weight="fill"/>}
                      {g.name}
                      <span className="text-[11px] font-normal text-zinc-500">{members.length} campaign{members.length === 1 ? "" : "s"}</span>
                    </div>
                    {typeof g.reached === "number" && g.mailable_total > 0 && (
                      <div className="text-[11px] font-normal text-zinc-500 mt-0.5 pl-[42px]" data-testid={`folder-countdown-${g.id}`}>
                        {g.reached.toLocaleString()} of {g.mailable_total.toLocaleString()} mailable contacts reached — {" "}
                        {g.mailable_total - g.reached > 0
                          ? <span className="text-sky-700 font-medium">{(g.mailable_total - g.reached).toLocaleString()} still to go</span>
                          : <span className="text-emerald-700 font-medium">everyone covered</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-[11px] text-zinc-400 uppercase tracking-wide">overall</td>
                  <td className="px-3 py-2.5 text-right font-medium">{t.recipients.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-right font-medium" data-testid={`folder-sent-${g.id}`}>{t.sent.toLocaleString()}</td>
                  <td className={`px-3 py-2.5 text-right ${t.bounced > 0 ? "text-rose-600 font-medium" : "text-zinc-400"}`}>{t.bounced.toLocaleString()}</td>
                  <td className={`px-3 py-2.5 text-right ${t.unsubscribed > 0 ? "text-zinc-700 font-medium" : "text-zinc-400"}`}>{t.unsubscribed.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-right font-medium" data-testid={`folder-openrate-${g.id}`}>{pct(t.unique_opens, t.sent)}%</td>
                  <td className="px-3 py-2.5 text-right font-medium">{pct(t.unique_clicks, t.sent)}%</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <button data-testid={`folder-report-${g.id}`} onClick={() => nav(`/campaigns/folder/${g.id}`)} title="Open the aggregated folder report"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-indigo-200 text-indigo-700 hover:border-indigo-400 hover:bg-indigo-50 mr-2 align-middle">
                      <ChartBar size={11}/> Report
                    </button>
                    <button data-testid={`folder-next-wave-${g.id}`} onClick={() => nextWave(g)} title="Duplicate the latest campaign onto the newest warm-up list"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-sky-200 text-sky-700 hover:border-sky-400 hover:bg-sky-50 mr-2 align-middle">
                      <PaperPlaneTilt size={11}/> New wave
                    </button>
                    <button data-testid={`folder-rename-${g.id}`} onClick={() => renameFolder(g)} title="Rename folder" className="text-zinc-400 hover:text-zinc-900 mr-2"><PencilSimple size={14}/></button>
                    <button data-testid={`folder-delete-${g.id}`} onClick={() => deleteFolder(g)} title="Delete folder (keeps campaigns)" className="text-zinc-400 hover:text-rose-700"><Trash size={14}/></button>
                  </td>
                </tr>,
                ...(!isCollapsed ? members.map(c => (
                  <CampaignRow key={c.id} c={c} groups={groups} nav={nav} onMove={moveCampaign} onDeleted={load} indent />
                )) : []),
                ...(!isCollapsed && members.length === 0 ? [
                  <tr key={`${g.id}-empty`} className="border-b border-zinc-100">
                    <td colSpan={9} className="px-10 py-3 text-xs text-zinc-400">Empty folder — move campaigns here with the folder dropdown on any row.</td>
                  </tr>] : []),
              ];
            })}
            {ungrouped.map(c => (
              <CampaignRow key={c.id} c={c} groups={groups} nav={nav} onMove={moveCampaign} onDeleted={load} />
            ))}
            {items.length === 0 && groups.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-sm text-zinc-500">No campaigns yet</td></tr>}
          </tbody>
        </table>
      </div>

      {open && <NewCampaignModal groups={groups} onClose={() => setOpen(false)} onSaved={(id) => { setOpen(false); nav(`/campaigns/${id}`); }}/>}
      {newFolder && <NewFolderModal onClose={() => setNewFolder(false)} onSaved={() => { setNewFolder(false); load(); }}/>}
    </div>
  );
}

function CampaignRow({ c, groups, nav, onMove, onDeleted, indent = false }) {
  const sent = c.stats?.sent || 0;
  const openR = sent ? ((c.stats.unique_opens / sent) * 100).toFixed(1) : 0;
  const clickR = sent ? ((c.stats.unique_clicks / sent) * 100).toFixed(1) : 0;
  return (
    <tr className="border-b border-zinc-100 hover:bg-zinc-50/60 cursor-pointer" onClick={() => nav(`/campaigns/${c.id}`)} data-testid={`campaign-${c.name}`}>
      <td className={`px-3 py-2.5 ${indent ? "pl-10" : ""}`}>
        <div className="font-medium text-zinc-900">{c.name}</div>
        <div className="text-[11px] text-zinc-500 truncate max-w-md">{c.subject}</div>
      </td>
      <td className="px-3 py-2.5"><StatusBadge value={c.status}/></td>
      <td className="px-3 py-2.5 text-right">{c.stats?.recipients?.toLocaleString() || 0}</td>
      <td className="px-3 py-2.5 text-right">{sent.toLocaleString()}</td>
      <td className={`px-3 py-2.5 text-right ${(c.stats?.bounced || 0) > 0 ? "text-rose-600 font-medium" : "text-zinc-400"}`} data-testid={`bounced-${c.id}`}>{(c.stats?.bounced || 0).toLocaleString()}</td>
      <td className={`px-3 py-2.5 text-right ${(c.stats?.unsubscribed || 0) > 0 ? "text-zinc-700 font-medium" : "text-zinc-400"}`} data-testid={`unsubs-${c.id}`}>{(c.stats?.unsubscribed || 0).toLocaleString()}</td>
      <td className="px-3 py-2.5 text-right">{openR}%</td>
      <td className="px-3 py-2.5 text-right">{clickR}%</td>
      <td className="px-3 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <select data-testid={`folder-select-${c.id}`} value={c.group_id || ""} onChange={(e) => onMove(c.id, e.target.value)}
          title="Move to folder"
          className="h-6 max-w-[110px] rounded border border-zinc-200 bg-white px-1 text-[11px] text-zinc-600 mr-2">
          <option value="">No folder</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <button onClick={async () => { if(window.confirm("Delete?")) { await api.deleteCampaign(c.id); onDeleted(); }}} className="text-zinc-400 hover:text-rose-700"><Trash size={14}/></button>
      </td>
    </tr>
  );
}

function NewFolderModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const submit = async () => {
    await api.createCampaignGroup(name.trim());
    toast.success("Folder created");
    onSaved();
  };
  return (
    <Modal title="New folder" onClose={onClose}>
      <Field label="Folder name"><input data-testid="folder-name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && name.trim() && submit()} className="modal-input" placeholder="MediPICs launch"/></Field>
      <p className="text-xs text-zinc-500 -mt-1 mb-2">Group the waves of one campaign under a single header with overall stats.</p>
      <ModalFooter onClose={onClose}>
        <button data-testid="folder-create" disabled={!name.trim()} onClick={submit} className="modal-btn-primary">Create</button>
      </ModalFooter>
    </Modal>
  );
}

function NewCampaignModal({ groups, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [groupId, setGroupId] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const submit = async () => {
    let gid = groupId === "__new__" ? null : (groupId || null);
    if (groupId === "__new__" && newFolderName.trim()) {
      const g = await api.createCampaignGroup(newFolderName.trim());
      gid = g.id;
    }
    const c = await api.createCampaign({
      name, subject: "", html: "", audience_type: "all",
      group_id: gid,
      editor_mode: "blocks",
      blocks: [
        { type: "logo" },
        { type: "hero", headline: "A headline that wows", subtext: "One sentence that makes them want to read on.", button_label: "Learn more", button_url: "https://", bg_color: "#005191", text_color: "#ffffff" },
        { type: "text", html: "<p>Hi {{first_name}},</p><p>Write something compelling here…</p>" },
        { type: "button", label: "Call to action", url: "https://", bg_color: "#005191" },
        { type: "text", html: "<p>Best regards,<br/><strong>The Medicys team</strong></p>" },
      ],
    });
    toast.success("Created");
    onSaved(c.id);
  };
  return (
    <Modal title="New campaign" onClose={onClose}>
      <Field label="Campaign name"><input data-testid="cmp-name" value={name} onChange={(e) => setName(e.target.value)} className="modal-input" placeholder="Q1 newsletter"/></Field>
      <Field label="Folder (optional)">
        <select data-testid="cmp-folder-select" value={groupId} onChange={(e) => setGroupId(e.target.value)} className="modal-input">
          <option value="">No folder</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          <option value="__new__">＋ New folder…</option>
        </select>
      </Field>
      {groupId === "__new__" && (
        <Field label="New folder name"><input data-testid="cmp-new-folder-name" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} className="modal-input" placeholder="MediPICs launch"/></Field>
      )}
      <ModalFooter onClose={onClose}>
        <button data-testid="cmp-create" disabled={!name || (groupId === "__new__" && !newFolderName.trim())} onClick={submit} className="modal-btn-primary">Continue</button>
      </ModalFooter>
    </Modal>
  );
}
